import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Save, ArrowLeft, Clock, CheckCircle, AlertCircle, 
  Bold, Italic, Heading1, Heading2, List, ListOrdered,
  Undo, Redo, Download, Eye, Edit, History, Users, Plus, X, Mail, Image,
  Share2, Link, Copy, ExternalLink, MessageSquare, Activity, Cloud, Unlink
} from 'lucide-react'
import api from '../lib/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

const ProposalEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const editorRef = useRef(null)
  
  const [proposal, setProposal] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [versions, setVersions] = useState([])
  const [previewMode, setPreviewMode] = useState(false)
  const [showReviewersModal, setShowReviewersModal] = useState(false)
  const [reviewers, setReviewers] = useState([])
  const [newReviewerEmail, setNewReviewerEmail] = useState('')
  const [newReviewerName, setNewReviewerName] = useState('')
  const [uploadingImage, setUploadingImage] = useState(false)
  const imageInputRef = useRef(null)
  
  // Share link state
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareLink, setShareLink] = useState(null)
  const [sharePassword, setSharePassword] = useState('')
  const [shareExpiryDays, setShareExpiryDays] = useState(30)
  const [shareAllowComments, setShareAllowComments] = useState(true)
  const [creatingShareLink, setCreatingShareLink] = useState(false)
  const [showViewLogs, setShowViewLogs] = useState(false)
  const [viewLogs, setViewLogs] = useState([])
  const [externalComments, setExternalComments] = useState([])
  
  // Lead linking state
  const [showLinkLeadModal, setShowLinkLeadModal] = useState(false)
  const [leads, setLeads] = useState([])
  const [leadSearchTerm, setLeadSearchTerm] = useState('')
  const [linkingLead, setLinkingLead] = useState(false)
  const [searchingLeads, setSearchingLeads] = useState(false)
  const [leadPage, setLeadPage] = useState(1)
  const [hasMoreLeads, setHasMoreLeads] = useState(false)
  const searchTimeoutRef = useRef(null)
  
  // Editor content state
  const [editorContent, setEditorContent] = useState('')
  const [title, setTitle] = useState('')
  const [executiveSummary, setExecutiveSummary] = useState('')
  
  // Auto-save timer ref
  const autoSaveTimerRef = useRef(null)

  useEffect(() => {
    fetchProposal()
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current)
      }
    }
  }, [id])

  // Debounced search effect for leads
  useEffect(() => {
    if (!showLinkLeadModal) return
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchLeadsFromZoho(leadSearchTerm, 1)
    }, 300)
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [leadSearchTerm, showLinkLeadModal])

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const fetchProposal = async () => {
    try {
      const response = await api.get(`/proposals/${id}`)
      const data = response.data
      setProposal(data)
      setTitle(data.title || '')
      setExecutiveSummary(data.executiveSummary || '')
      setReviewers(data.reviewers || [])
      
      // Convert sections to markdown format
      const sectionsMarkdown = data.sections?.map(s => 
        `## ${s.title}\n\n${s.content}`
      ).join('\n\n') || ''
      setEditorContent(sectionsMarkdown)
      
      setLastSaved(new Date(data.updatedAt))
      
      // Fetch share link status
      fetchShareLinkStatus()
      fetchExternalComments()
    } catch (error) {
      console.error('Failed to fetch proposal:', error)
      toast.error('Failed to load proposal')
      navigate('/proposals')
    } finally {
      setLoading(false)
    }
  }

  const searchLeadsFromZoho = async (searchTerm, page = 1) => {
    setSearchingLeads(true)
    try {
      const response = await api.get('/crm/zoho/leads/search', {
        params: { q: searchTerm, page, limit: 20 }
      })
      if (page === 1) {
        setLeads(response.data.leads || [])
      } else {
        setLeads(prev => [...prev, ...(response.data.leads || [])])
      }
      setHasMoreLeads(response.data.hasMore || false)
      setLeadPage(page)
    } catch (error) {
      console.error('Failed to search leads:', error)
      toast.error('Failed to search leads from Zoho')
    } finally {
      setSearchingLeads(false)
    }
  }

  const loadMoreLeads = () => {
    if (!searchingLeads && hasMoreLeads) {
      searchLeadsFromZoho(leadSearchTerm, leadPage + 1)
    }
  }

  const handleLinkLead = async (lead) => {
    setLinkingLead(true)
    try {
      const response = await api.post(`/proposals/${id}/link-lead`, { 
        leadId: lead._id || null,
        crmId: lead.crmId,
        leadData: {
          name: lead.name,
          email: lead.email,
          company: lead.company,
          phone: lead.phone
        }
      })
      toast.success('Lead linked successfully! Deal created in Zoho CRM.')
      setShowLinkLeadModal(false)
      setLeadSearchTerm('')
      setLeads([])
      setProposal(response.data.proposal)
    } catch (error) {
      console.error('Failed to link lead:', error)
      toast.error(error.response?.data?.error || 'Failed to link lead')
    } finally {
      setLinkingLead(false)
    }
  }

  const handleUnlinkLead = async () => {
    if (!confirm('Are you sure you want to unlink this lead?')) return
    try {
      await api.delete(`/proposals/${id}/link-lead`)
      toast.success('Lead unlinked successfully')
      fetchProposal()
    } catch (error) {
      console.error('Failed to unlink lead:', error)
      toast.error('Failed to unlink lead')
    }
  }

  const filteredLeads = leads.filter(lead => 
    lead.name?.toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
    lead.company?.toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
    lead.email?.toLowerCase().includes(leadSearchTerm.toLowerCase())
  )
  
  const fetchShareLinkStatus = async () => {
    try {
      const response = await api.get(`/proposals/${id}/share-link`)
      if (response.data.hasShareLink) {
        setShareLink(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch share link status:', error)
    }
  }
  
  const fetchExternalComments = async () => {
    try {
      const response = await api.get(`/proposals/${id}/external-comments`)
      setExternalComments(response.data.comments || [])
    } catch (error) {
      console.error('Failed to fetch external comments:', error)
    }
  }
  
  const fetchViewLogs = async () => {
    try {
      const response = await api.get(`/proposals/${id}/share-link/views`)
      setViewLogs(response.data.views || [])
      setShowViewLogs(true)
    } catch (error) {
      toast.error('Failed to fetch view logs')
    }
  }
  
  const createShareLink = async () => {
    if (!sharePassword || sharePassword.length < 4) {
      toast.error('Password must be at least 4 characters')
      return
    }
    
    setCreatingShareLink(true)
    try {
      const response = await api.post(`/proposals/${id}/share-link`, {
        password: sharePassword,
        expiresInDays: shareExpiryDays,
        allowComments: shareAllowComments
      })
      setShareLink({
        hasShareLink: true,
        shareUrl: response.data.shareUrl,
        isActive: true,
        expiresAt: response.data.expiresAt,
        allowComments: response.data.allowComments,
        viewCount: 0
      })
      setSharePassword('')
      toast.success('Share link created!')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create share link')
    } finally {
      setCreatingShareLink(false)
    }
  }
  
  const deactivateShareLink = async () => {
    try {
      await api.delete(`/proposals/${id}/share-link`)
      setShareLink(null)
      toast.success('Share link deactivated')
    } catch (error) {
      toast.error('Failed to deactivate share link')
    }
  }
  
  const copyShareLink = () => {
    if (shareLink?.shareUrl) {
      navigator.clipboard.writeText(shareLink.shareUrl)
      toast.success('Link copied to clipboard!')
    }
  }

  const fetchVersionHistory = async () => {
    try {
      const response = await api.get(`/proposals/${id}/versions`)
      setVersions(response.data || [])
    } catch (error) {
      console.error('Failed to fetch versions:', error)
      // If endpoint doesn't exist, show current version only
      setVersions([{
        version: proposal?.version || 1,
        updatedAt: proposal?.updatedAt,
        title: proposal?.title
      }])
    }
  }

  // Parse markdown sections back to array
  const parseSections = (markdown) => {
    const sectionRegex = /## (.+?)\n\n([\s\S]*?)(?=\n\n## |$)/g
    const sections = []
    let match
    while ((match = sectionRegex.exec(markdown)) !== null) {
      sections.push({
        title: match[1].trim(),
        content: match[2].trim(),
        order: sections.length
      })
    }
    // If no sections parsed but content exists, create a single section
    if (sections.length === 0 && markdown.trim()) {
      sections.push({
        title: 'Content',
        content: markdown.trim(),
        order: 0
      })
    }
    return sections
  }

  // Auto-save with debounce
  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current)
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      handleSave(true)
    }, 3000) // Auto-save after 3 seconds of inactivity
  }, [title, executiveSummary, editorContent])

  const handleContentChange = (newContent) => {
    setEditorContent(newContent)
    setHasUnsavedChanges(true)
    triggerAutoSave()
  }

  const handleTitleChange = (newTitle) => {
    setTitle(newTitle)
    setHasUnsavedChanges(true)
    triggerAutoSave()
  }

  const handleSummaryChange = (newSummary) => {
    setExecutiveSummary(newSummary)
    setHasUnsavedChanges(true)
    triggerAutoSave()
  }

  const handleSave = async (isAutoSave = false) => {
    if (saving) return
    
    setSaving(true)
    try {
      const sections = parseSections(editorContent)
      
      const updateData = {
        title,
        executiveSummary,
        sections,
        reviewers,
        // Increment version on manual save
        version: isAutoSave ? proposal.version : (proposal.version || 1) + 1
      }

      const response = await api.put(`/proposals/${id}`, updateData)
      
      setProposal(response.data)
      setLastSaved(new Date())
      setHasUnsavedChanges(false)
      
      if (!isAutoSave) {
        toast.success(`Saved! Version ${response.data.version}`)
      }
    } catch (error) {
      console.error('Save failed:', error)
      if (!isAutoSave) {
        toast.error('Failed to save proposal')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleManualSave = () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current)
    }
    handleSave(false)
  }

  const handleAddReviewer = () => {
    if (!newReviewerEmail || !newReviewerEmail.includes('@')) {
      toast.error('Please enter a valid email address')
      return
    }
    
    const newReviewer = {
      email: newReviewerEmail,
      name: newReviewerName || newReviewerEmail.split('@')[0],
      status: 'Pending'
    }
    
    setReviewers([...reviewers, newReviewer])
    setNewReviewerEmail('')
    setNewReviewerName('')
    setHasUnsavedChanges(true)
  }

  const handleRemoveReviewer = (index) => {
    const updated = reviewers.filter((_, i) => i !== index)
    setReviewers(updated)
    setHasUnsavedChanges(true)
  }

  const handleRollback = async (targetVersion) => {
    if (!window.confirm(`Are you sure you want to restore Version ${targetVersion}? Your current changes will be saved to history.`)) {
      return
    }

    try {
      const response = await api.post(`/proposals/${id}/rollback/${targetVersion}`)
      toast.success(`Restored to Version ${targetVersion}`)
      
      // Reload the proposal
      const updatedProposal = response.data.proposal
      setProposal(updatedProposal)
      setTitle(updatedProposal.title || '')
      setExecutiveSummary(updatedProposal.executiveSummary || '')
      
      const sectionsMarkdown = updatedProposal.sections?.map(s => 
        `## ${s.title}\n\n${s.content}`
      ).join('\n\n') || ''
      setEditorContent(sectionsMarkdown)
      
      setShowVersionHistory(false)
      setHasUnsavedChanges(false)
      fetchVersionHistory()
    } catch (error) {
      console.error('Rollback failed:', error)
      toast.error(error.response?.data?.error || 'Failed to restore version')
    }
  }

  // Toolbar actions for markdown formatting
  const insertMarkdown = (prefix, suffix = '') => {
    const textarea = editorRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = editorContent.substring(start, end)
    
    const newContent = 
      editorContent.substring(0, start) + 
      prefix + selectedText + suffix + 
      editorContent.substring(end)
    
    setEditorContent(newContent)
    setHasUnsavedChanges(true)
    triggerAutoSave()

    // Restore cursor position
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(
        start + prefix.length,
        end + prefix.length
      )
    }, 0)
  }

  const handleDownload = () => {
    const fullContent = `# ${title}\n\n## Executive Summary\n\n${executiveSummary}\n\n${editorContent}`
    const blob = new Blob([fullContent], { type: 'text/markdown' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${title || 'proposal'}.md`
    link.click()
    window.URL.revokeObjectURL(url)
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (PNG, JPG, GIF, WEBP)')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB')
      return
    }

    setUploadingImage(true)
    try {
      const formData = new FormData()
      formData.append('image', file)

      const response = await api.post('/proposals/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      // Insert markdown image syntax at cursor position
      const imageUrl = response.data.url
      const imageName = file.name.replace(/\.[^/.]+$/, '')
      insertMarkdown(`![${imageName}](${imageUrl})`, '')
      
      toast.success('Image uploaded successfully')
    } catch (error) {
      console.error('Image upload failed:', error)
      toast.error('Failed to upload image')
    } finally {
      setUploadingImage(false)
      // Reset input
      if (imageInputRef.current) {
        imageInputRef.current.value = ''
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading proposal...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Top Toolbar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Back & Title */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (hasUnsavedChanges) {
                    if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                      navigate('/proposals')
                    }
                  } else {
                    navigate('/proposals')
                  }
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-900 dark:text-gray-100"
                title="Back to Proposals"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="text-xl font-bold text-gray-900 dark:text-gray-100 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-2 py-1 w-96"
                  placeholder="Proposal Title"
                />
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 px-2">
                  <span>Version {proposal?.version || 1}</span>
                  <span>•</span>
                  {saving ? (
                    <span className="flex items-center text-yellow-600">
                      <Clock className="w-3 h-3 mr-1 animate-spin" />
                      Saving...
                    </span>
                  ) : hasUnsavedChanges ? (
                    <span className="flex items-center text-orange-600">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Unsaved changes
                    </span>
                  ) : lastSaved ? (
                    <span className="flex items-center text-green-600">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Saved {format(lastSaved, 'HH:mm:ss')}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className={`flex items-center px-3 py-2 rounded-lg text-sm ${
                  previewMode 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                {previewMode ? <Edit className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                {previewMode ? 'Edit' : 'Preview'}
              </button>
              <button
                onClick={() => setShowReviewersModal(true)}
                className="flex items-center px-3 py-2 hover:bg-gray-100 rounded-lg text-sm text-gray-700"
              >
                <Users className="w-4 h-4 mr-1" />
                Reviewers {reviewers.length > 0 && `(${reviewers.length})`}
              </button>
              <button
                onClick={() => {
                  fetchVersionHistory()
                  setShowVersionHistory(true)
                }}
                className="flex items-center px-3 py-2 hover:bg-gray-100 rounded-lg text-sm text-gray-700"
              >
                <History className="w-4 h-4 mr-1" />
                History
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center px-3 py-2 hover:bg-gray-100 rounded-lg text-sm text-gray-700"
              >
                <Download className="w-4 h-4 mr-1" />
                Export
              </button>
              <button
                onClick={() => { setShowLinkLeadModal(true); fetchLeads(); }}
                className={`flex items-center px-3 py-2 rounded-lg text-sm ${
                  proposal?.linkedLead 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                    : 'hover:bg-gray-100 text-gray-700 dark:hover:bg-gray-700 dark:text-gray-300'
                }`}
              >
                <Users className="w-4 h-4 mr-1" />
                {proposal?.linkedLead ? 'Lead Linked' : 'Link Lead'}
              </button>
              <button
                onClick={() => setShowShareModal(true)}
                className={`flex items-center px-3 py-2 rounded-lg text-sm ${
                  shareLink?.isActive 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                    : 'hover:bg-gray-100 text-gray-700 dark:hover:bg-gray-700 dark:text-gray-300'
                }`}
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share {shareLink?.isActive && `(${shareLink.viewCount || 0})`}
              </button>
              <button
                onClick={handleManualSave}
                disabled={saving || !hasUnsavedChanges}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </button>
            </div>
          </div>
        </div>

        {/* Formatting Toolbar */}
        {!previewMode && (
          <div className="border-t border-gray-100 px-4 py-2">
            <div className="max-w-7xl mx-auto flex items-center gap-1">
              <button
                onClick={() => insertMarkdown('**', '**')}
                className="p-2 hover:bg-gray-100 rounded"
                title="Bold"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                onClick={() => insertMarkdown('*', '*')}
                className="p-2 hover:bg-gray-100 rounded"
                title="Italic"
              >
                <Italic className="w-4 h-4" />
              </button>
              <div className="w-px h-6 bg-gray-200 mx-2" />
              <button
                onClick={() => insertMarkdown('# ')}
                className="p-2 hover:bg-gray-100 rounded"
                title="Heading 1"
              >
                <Heading1 className="w-4 h-4" />
              </button>
              <button
                onClick={() => insertMarkdown('## ')}
                className="p-2 hover:bg-gray-100 rounded"
                title="Heading 2 (Section)"
              >
                <Heading2 className="w-4 h-4" />
              </button>
              <div className="w-px h-6 bg-gray-200 mx-2" />
              <button
                onClick={() => insertMarkdown('- ')}
                className="p-2 hover:bg-gray-100 rounded"
                title="Bullet List"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => insertMarkdown('1. ')}
                className="p-2 hover:bg-gray-100 rounded"
                title="Numbered List"
              >
                <ListOrdered className="w-4 h-4" />
              </button>
              <div className="w-px h-6 bg-gray-200 mx-2" />
              <input
                type="file"
                ref={imageInputRef}
                onChange={handleImageUpload}
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                className="hidden"
              />
              <button
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadingImage}
                className="p-2 hover:bg-gray-100 rounded disabled:opacity-50"
                title="Insert Image"
              >
                {uploadingImage ? (
                  <Clock className="w-4 h-4 animate-spin" />
                ) : (
                  <Image className="w-4 h-4" />
                )}
              </button>
              <div className="flex-1" />
              <span className="text-xs text-gray-400">
                Use ## Section Title to create sections
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 max-w-5xl mx-auto w-full p-6">
        {previewMode ? (
          /* Preview Mode */
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 min-h-[600px]">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">{title}</h1>
            
            {executiveSummary && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Executive Summary</h2>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{executiveSummary}</p>
              </div>
            )}

            {parseSections(editorContent).map((section, index) => (
              <div key={index} className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">{section.title}</h2>
                <div 
                  className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap proposal-content"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
                {/* Render diagrams for this section */}
                {proposal?.diagrams?.filter(d => d.section?.toLowerCase().includes(section.title?.toLowerCase())).map((diagram, dIdx) => (
                  <div key={dIdx} className="my-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{diagram.title}</h4>
                    {diagram.renderedUrl ? (
                      <img src={diagram.renderedUrl} alt={diagram.title} className="max-w-full h-auto" />
                    ) : diagram.mermaidCode ? (
                      <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-x-auto">{diagram.mermaidCode}</pre>
                    ) : (
                      <p className="text-sm text-gray-500 italic">{diagram.description}</p>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {/* Diagrams Section */}
            {proposal?.diagrams?.length > 0 && (
              <div className="mb-8 border-t border-gray-200 dark:border-gray-700 pt-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Diagrams & Visuals</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {proposal.diagrams.map((diagram, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{diagram.title}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{diagram.description}</p>
                      {diagram.renderedUrl ? (
                        <img 
                          src={diagram.renderedUrl.startsWith('http') ? diagram.renderedUrl : `${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || ''}${diagram.renderedUrl}`} 
                          alt={diagram.title} 
                          className="max-w-full h-auto rounded border border-gray-200 dark:border-gray-600"
                        />
                      ) : diagram.mermaidCode ? (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-600">
                          <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{diagram.mermaidCode}</pre>
                        </div>
                      ) : (
                        <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded text-center text-gray-400">
                          <span className="text-sm">Diagram placeholder: {diagram.type}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Edit Mode */
          <div className="space-y-6">
            {/* Executive Summary */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Executive Summary
              </label>
              <textarea
                value={executiveSummary}
                onChange={(e) => handleSummaryChange(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Write a compelling executive summary..."
              />
            </div>

            {/* Main Content Editor */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Proposal Content
                <span className="text-gray-400 font-normal ml-2">
                  (Use ## Section Title to create sections)
                </span>
              </label>
              <textarea
                ref={editorRef}
                value={editorContent}
                onChange={(e) => handleContentChange(e.target.value)}
                rows={25}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm resize-none"
                placeholder={`## Introduction

Write your introduction here...

## Technical Approach

Describe your technical approach...

## Timeline & Deliverables

List your timeline and deliverables...`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Version History Modal */}
      {showVersionHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Version History</h3>
            <p className="text-sm text-gray-500 mb-4">Click "Restore" to rollback to a previous version.</p>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {/* Current Version */}
              <div className="p-3 rounded-lg border border-primary-500 bg-primary-50">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Version {proposal?.version || 1}</span>
                  <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded">
                    Current
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {proposal?.updatedAt ? format(new Date(proposal.updatedAt), 'MMM dd, yyyy HH:mm') : 'Now'}
                </p>
              </div>
              
              {/* Previous Versions */}
              {versions.length > 0 ? (
                versions.slice().reverse().map((v, i) => (
                  <div 
                    key={i} 
                    className="p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">Version {v.version}</span>
                        <p className="text-xs text-gray-400">{v.title}</p>
                      </div>
                      <button
                        onClick={() => handleRollback(v.version)}
                        className="text-xs px-3 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                      >
                        Restore
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {v.updatedAt ? format(new Date(v.updatedAt), 'MMM dd, yyyy HH:mm') : 'Unknown'}
                      {v.savedBy && <span className="ml-2 text-gray-400">• {v.savedBy}</span>}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No previous versions available</p>
              )}
            </div>
            <button
              onClick={() => setShowVersionHistory(false)}
              className="w-full mt-4 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Reviewers Modal */}
      {showReviewersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Manage Reviewers</h3>
            
            {/* Add Reviewer Form */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 mb-3">Add reviewers to get feedback on this proposal.</p>
              <div className="space-y-2">
                <input
                  type="text"
                  value={newReviewerName}
                  onChange={(e) => setNewReviewerName(e.target.value)}
                  placeholder="Reviewer Name (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newReviewerEmail}
                    onChange={(e) => setNewReviewerEmail(e.target.value)}
                    placeholder="Reviewer Email *"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={handleAddReviewer}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Reviewers List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {reviewers.length > 0 ? (
                reviewers.map((reviewer, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <Mail className="w-4 h-4 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{reviewer.name}</p>
                        <p className="text-xs text-gray-500">{reviewer.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        reviewer.status === 'Approved' ? 'bg-green-100 text-green-700' :
                        reviewer.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                        reviewer.status === 'Changes Requested' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {reviewer.status}
                      </span>
                      <button
                        onClick={() => handleRemoveReviewer(index)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No reviewers added yet</p>
              )}
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowReviewersModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              {hasUnsavedChanges && (
                <button
                  onClick={handleManualSave}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Share Link Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                External Share Link
              </h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {shareLink?.isActive ? (
              <div className="space-y-4">
                {/* Active Share Link */}
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400 mb-2">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Share link is active</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={shareLink.shareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                    />
                    <button
                      onClick={copyShareLink}
                      className="p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
                      title="Copy link"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <a
                      href={shareLink.shareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
                      title="Open link"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{shareLink.viewCount || 0}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Views</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{externalComments.length}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Comments</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {shareLink.expiresAt ? new Date(shareLink.expiresAt).toLocaleDateString() : 'N/A'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Expires</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={fetchViewLogs}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                  >
                    <Activity className="w-4 h-4" />
                    View Logs
                  </button>
                  <button
                    onClick={deactivateShareLink}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 text-sm"
                  >
                    <X className="w-4 h-4" />
                    Deactivate
                  </button>
                </div>

                {/* External Comments */}
                {externalComments.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      External Comments ({externalComments.length})
                    </h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {externalComments.map((comment, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-900 dark:text-white">{comment.reviewerName}</span>
                            <span className="text-xs text-gray-500">{new Date(comment.timestamp).toLocaleString()}</span>
                          </div>
                          {comment.sectionTitle !== 'General' && (
                            <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded">
                              {comment.sectionTitle}
                            </span>
                          )}
                          <p className="text-gray-700 dark:text-gray-300 mt-1">{comment.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Create Share Link Form */}
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Create a password-protected link to share this proposal with external reviewers.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password *
                  </label>
                  <input
                    type="text"
                    value={sharePassword}
                    onChange={(e) => setSharePassword(e.target.value)}
                    placeholder="Enter a password (min 4 characters)"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Link Expires In
                  </label>
                  <select
                    value={shareExpiryDays}
                    onChange={(e) => setShareExpiryDays(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="allowComments"
                    checked={shareAllowComments}
                    onChange={(e) => setShareAllowComments(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <label htmlFor="allowComments" className="text-sm text-gray-700 dark:text-gray-300">
                    Allow external reviewers to leave comments
                  </label>
                </div>

                <button
                  onClick={createShareLink}
                  disabled={creatingShareLink || !sharePassword}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  <Link className="w-4 h-4" />
                  {creatingShareLink ? 'Creating...' : 'Create Share Link'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Link Lead Modal */}
      {showLinkLeadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Link Lead from Zoho CRM</h3>
              <button onClick={() => { setShowLinkLeadModal(false); setLeadSearchTerm(''); setLeads([]); }} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Type to search leads in Zoho CRM..."
                  value={leadSearchTerm}
                  onChange={(e) => setLeadSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  autoFocus
                />
                {searchingLeads && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 min-h-[200px]">
              {searchingLeads && leads.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">Searching Zoho CRM...</span>
                </div>
              ) : leads.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {leadSearchTerm ? 'No leads found matching your search' : 'Start typing to search leads in Zoho CRM'}
                </p>
              ) : (
                <div className="space-y-2">
                  {leads.map((lead, index) => (
                    <div
                      key={lead.crmId || index}
                      onClick={() => !linkingLead && handleLinkLead(lead)}
                      className={`flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${linkingLead ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                          <span className="text-primary-600 dark:text-primary-400 text-sm font-semibold">
                            {lead.name?.charAt(0) || 'L'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{lead.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{lead.company} • {lead.email}</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs">
                        <Cloud className="w-3 h-3 mr-1" /> Zoho
                      </span>
                    </div>
                  ))}
                  
                  {hasMoreLeads && (
                    <button
                      onClick={loadMoreLeads}
                      disabled={searchingLeads}
                      className="w-full py-2 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                    >
                      {searchingLeads ? 'Loading...' : 'Load More'}
                    </button>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-b-xl">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Linking a lead with Zoho CRM ID will create a Deal in Zoho CRM automatically.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* View Logs Modal */}
      {showViewLogs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Activity className="w-5 h-5" />
                View Logs
              </h2>
              <button
                onClick={() => setShowViewLogs(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {viewLogs.length > 0 ? (
                viewLogs.map((log, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-gray-600 dark:text-gray-400">{log.ip}</span>
                      <span className="text-xs text-gray-500">{new Date(log.timestamp).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{log.userAgent}</p>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">No views yet</p>
              )}
            </div>

            <button
              onClick={() => setShowViewLogs(false)}
              className="w-full mt-4 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProposalEditor
